import { useState } from 'react';
// @mui
import { Stack, Typography, Divider, Box, Container, Grid, Card } from '@mui/material';
import { Helmet } from 'react-helmet-async';
// components
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import Alert from '@mui/material/Alert';
import axiosInstance from '../../utils/axiosInstance';
import CameraInfo from '../../components/camera-info/cameraInfo';
import SingleSelector from '../../components/single-selector/singleSelector';

export default function NvStreamerConfiguration() {
  const { enqueueSnackbar } = useSnackbar();
  const [camera, setCamera] = useState(null);
  const [cameraInfo, setCameraInfo] = useState(null);
  const [infoFetchStatus, setInfoFetchStatus] = useState('uninitialized');
  const cameraList = useSelector((state) => state.app.cameraList);

  const fetchCameraDetails = (camera) => {
    if (!camera) {
      return;
    }
    setInfoFetchStatus('pending');
    axiosInstance
      .get(`${window.location.protocol}//${camera.host}/api/v1/sensor/${camera.sensorId}/info`)
      .then(async (response) => {
        setInfoFetchStatus('success');
        console.debug('fetchInfo response: ', response.data);
        setCameraInfo(response.data);
      })
      .catch((error) => {
        setInfoFetchStatus('error');
        if (error.response.data == null)
          enqueueSnackbar(`Error - ${error.response.status}`, {
            variant: 'error',
            anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
          });
        else
          enqueueSnackbar(`Error - ${error.response.data.error_message}`, {
            variant: 'error',
            anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
          });
      });
  };

  const handleCameraSelect = (cam) => {
    setCamera(cam);
    fetchCameraDetails(cam);
    if (!cam) {
      setCameraInfo(null);
    }
  };

  const cameraInfoPresent = camera != null && cameraInfo != null;

  return (
    <>
      <Helmet>
        <title> Camera details </title>
      </Helmet>

      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 5 }}>
          Configuration
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={12}>
            <Card>
              <Box sx={{ p: 3 }}>
                <Stack spacing={3} sx={{ mb: 2 }}>
                  <SingleSelector
                    id="file-details"
                    placeholder="Type to search"
                    label="Select file"
                    value={camera}
                    onChange={handleCameraSelect}
                    getOptionLabel={(option) => {
                      if (option.remoteDeviceName && option.remoteDeviceLocation) {
                        return `${option.name} at ${option.remoteDeviceName} (${option.remoteDeviceLocation})`;
                      } if (option.remoteDeviceName) {
                        return `${option.name} at ${option.remoteDeviceName}`;
                      }
                      return option.name;

                    }}
                    options={cameraList}
                  />
                  {camera != null && cameraInfo != null && infoFetchStatus !== 'pending' && (
                    <Alert severity="success">{`file info found for ${camera.name}`}</Alert>
                  )}
                  {camera != null && cameraInfo == null && infoFetchStatus !== 'pending' && (
                    <Alert severity="error">{`Failed to get configuration for ${camera.name}`}</Alert>
                  )}
                </Stack>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <Card>
              <Box sx={{ p: 3 }}>
                <Stack spacing={3} sx={{ mb: 2 }}>
                  <Divider sx={{ my: 2 }}>
                    <Typography variant="h5" sx={{ color: 'text.secondary' }}>
                      File details
                    </Typography>
                  </Divider>
                  {cameraInfoPresent && (
                    <CameraInfo camera={camera} cameraInfo={cameraInfo} key={`${cameraInfo.sensorId}_info`} />
                  )}
                </Stack>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
